import { Plus, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { INTEGRATION_REGISTRY } from "@/lib/integration-registry"
import type { AgentResource } from "@/types"

interface AgentResourcesFormProps {
  kinds: string[]
  accountUid: string
  resourcesByKind: Record<string, AgentResource[]>
  onResourcesChange: (resourcesByKind: Record<string, AgentResource[]>) => void
}

export default function AgentResourcesForm({
  kinds,
  resourcesByKind,
  onResourcesChange,
}: AgentResourcesFormProps) {
  if (kinds.length === 0) return null

  // Build a flat list of all available resource types from selected integrations
  const availableResourceTypes = kinds.flatMap((kind) => {
    const integration = INTEGRATION_REGISTRY[kind]
    if (!integration) return []
    return integration.resourceTypes.map((rt) => ({
      ...rt,
      kind,
    }))
  })

  // Build a flat list of all resources with their kind for rendering
  const flatResources: {
    kind: string
    index: number
    resource: AgentResource
  }[] = []
  for (const kind of kinds) {
    const resources = resourcesByKind[kind] || []
    resources.forEach((resource, index) => {
      flatResources.push({ kind, index, resource })
    })
  }

  const findKindForResourceType = (
    resourceType: string,
  ): string | undefined => {
    for (const kind of kinds) {
      const integration = INTEGRATION_REGISTRY[kind]
      if (integration?.resourceTypes.some((rt) => rt.value === resourceType)) {
        return kind
      }
    }
    return undefined
  }

  const addResource = () => {
    if (availableResourceTypes.length === 0) return
    const firstType = availableResourceTypes[0]
    const kind = firstType.kind
    const updated = {
      ...resourcesByKind,
      [kind]: [
        ...(resourcesByKind[kind] || []),
        { resource_type: firstType.value, name: "", label: "" },
      ],
    }
    onResourcesChange(updated)
  }

  const removeResource = (kind: string, index: number) => {
    const updated = { ...resourcesByKind }
    const resources = [...(updated[kind] || [])]
    resources.splice(index, 1)
    if (resources.length === 0) {
      delete updated[kind]
    } else {
      updated[kind] = resources
    }
    onResourcesChange(updated)
  }

  const updateResourceLabel = (kind: string, index: number, label: string) => {
    const updated = { ...resourcesByKind }
    const resources = [...(updated[kind] || [])]
    resources[index] = { ...resources[index], label }
    updated[kind] = resources
    onResourcesChange(updated)
  }

  const updateResourceType = (
    currentKind: string,
    index: number,
    newResourceType: string,
  ) => {
    const newKind = findKindForResourceType(newResourceType)
    if (!newKind) return

    const updated = { ...resourcesByKind }

    if (newKind === currentKind) {
      // Same kind — just update the resource type in place
      const resources = [...(updated[currentKind] || [])]
      resources[index] = { ...resources[index], resource_type: newResourceType }
      updated[currentKind] = resources
    } else {
      // Moving to a different kind bucket
      const oldResources = [...(updated[currentKind] || [])]
      const [moved] = oldResources.splice(index, 1)
      if (oldResources.length === 0) {
        delete updated[currentKind]
      } else {
        updated[currentKind] = oldResources
      }
      updated[newKind] = [
        ...(updated[newKind] || []),
        { ...moved, resource_type: newResourceType },
      ]
    }

    onResourcesChange(updated)
  }

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">Resources</Label>

      {flatResources.length > 0 && (
        <div className="space-y-2">
          {flatResources.map((item, flatIndex) => (
            <div key={flatIndex} className="flex gap-2">
              <Select
                value={item.resource.resource_type}
                onValueChange={(val) =>
                  updateResourceType(item.kind, item.index, val)
                }
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableResourceTypes.map((rt) => (
                    <SelectItem key={rt.value} value={rt.value}>
                      {rt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                className="flex-1"
                placeholder="Label"
                value={item.resource.label ?? ""}
                onChange={(e) =>
                  updateResourceLabel(item.kind, item.index, e.target.value)
                }
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0"
                onClick={() => removeResource(item.kind, item.index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Button type="button" variant="outline" onClick={addResource}>
        <Plus className="h-4 w-4" />
        Add resource
      </Button>
    </div>
  )
}
